// Sidebar configuration derived from the @module registry
// (src/modules/index.js). The registry is the single source of truth —
// this file only knows how to translate + apply the permission filter
// and turn the registry into the shape `Sidebar.jsx` expects.
//
// Permission checks go through `hasPermission` (src/utils/branchPermissions.js),
// which reads the branch-scoped payload cached in
// `localStorage['branchPermissions']`. A module is visible only when the
// payload grants it an explicit `view` action.

import { useEffect, useMemo, useState } from 'react'

import { PERMISSIONS } from '../../constants/permission'
import { PERMISSIONS_UPDATED_EVENT } from '../../hooks/usePermissionEvents'
import { useLanguage } from '../../i18n'
import { modules, moduleList } from '../../modules'
import { hasPermission } from '../../utils/branchPermissions'
import { userStorage } from '../../utils/userStorage'

// Build a sidebar-shaped entry from a registry module. Sidebar.jsx reads
// `id`, `label`, `icon`, `path`, `subItems`, and `moduleId` — the extra
// `labelKey` keeps the original translation key around for context.
function toNavItem(module) {
  return {
    id: module.key,
    labelKey: module.labelKey,
    label: module.label,
    icon: module.icon,
    path: module.path,
    moduleId: module.key,
  }
}

function toNavSubItem(module) {
  return {
    id: module.key,
    labelKey: module.labelKey,
    label: module.label,
    icon: module.icon,
    path: module.path,
    moduleId: module.key,
  }
}

// Decide whether a registry entry should render:
//
//   - `permissionKeys` missing  → "common", always visible
//   - `permissionKeys: []`      → "common", always visible
//   - `permissionKeys: [...]`   → visible when ANY listed slug is granted
//     an explicit `view` action by the branch-scoped permission payload
//     (see utils/branchPermissions.js)
//
// The empty-or-missing case is reserved for grouping-only parents (a
// nav group whose visibility is driven by its children) — every leaf
// item, including "What's New" and "Audit Trails", now carries its own
// `permissionKeys` so the payload decides whether it renders.
//
// `requiresCompanyType: true` adds a second gate: only COMPANY-typed
// accounts (per the cached `/me` payload) can see this entry. It's an
// optional extra gate on top of the permission check — no registry entry
// uses it today, because the branch-scoped payload already encodes who
// may see what. While `/me` is still loading userStorage is empty, so
// `userType` is undefined and the gate is treated as satisfied
// (soft-allow) — same as the permission matcher — so the sidebar doesn't
// flash empty before React commits the cache.
function isVisible(entry) {
  const keys = entry.permissionKeys
  if (
    Array.isArray(keys) &&
    keys.length > 0 &&
    !keys.some((k) => hasPermission(k, PERMISSIONS.VIEW))
  ) {
    return false
  }
  if (entry.requiresCompanyType) {
    const userType = userStorage.getUserDetails()?.type
    if (userType !== undefined && userType !== 'COMPANY') return false
  }
  return true
}

// Group child modules under their parent. A parent with no children left
// after filtering is dropped entirely (the sidebar collapses empty
// groups). Standalone modules (`children.length === 0`) survive only
// when the permission check passes for them.
function buildWithChildren(parent, allModules) {
  const children = allModules
    .filter((m) => m.parent === parent.key)
    .sort((a, b) => a.order - b.order)

  if (children.length === 0) {
    // Standalone (leaf) module — gate by permission + (optionally) the
    // COMPANY-type flag; drop entirely if either check fails.
    return isVisible(parent) ? toNavItem(parent) : null
  }

  // Parent module — keep only when at least one child is visible.
  // The parent itself uses the same visibility check (its own
  // `permissionKeys`, if any) so a manually-restricted parent group
  // is honoured even when the children all have permission.
  if (!isVisible(parent)) return null
  const visibleChildren = children.filter((c) => isVisible(c)).map(toNavSubItem)
  if (visibleChildren.length === 0) return null

  return {
    ...toNavItem(parent),
    // Sidebar uses `path` to decide between NavLink vs. collapsible
    // button; a parent has no path of its own.
    path: null,
    subItems: visibleChildren,
  }
}

// Hook used by Sidebar — returns a permission-aware config whose labels
// are resolved through the active locale's dictionary (or English
// fallback). Re-runs on language change so the menu updates immediately
// when the user switches language.
//
// Permission checks go through `hasPermission` directly (the matcher reads
// the cached branch permissions, so the latest payload wins on every call).
// Reactivity to permission changes — a branch switch, a role edit — is
// preserved by subscribing to the `mdg:permissions-updated` window event
// the previous `useEffectivePermissions` hook consumed; we bump a tick
// counter on each fire so the useMemo below sees a fresh dependency and
// re-derives the tree.
//
// Cold-load behaviour: the branch permission payload is written during
// branch selection, so it is already in localStorage by the time the app
// shell renders. If it is missing, the matcher denies every module and the
// sidebar stays empty until the payload is fetched.
export function useNavigationConfig() {
  const { t, lang } = useLanguage()

  // Tick counter — incremented on every PERMISSIONS_UPDATED_EVENT so
  // the useMemo re-runs. The actual permission decisions are made
  // synchronously by `hasPermission`, so the tick value
  // itself is unused beyond triggering the recomputation.
  const [permissionsTick, setPermissionsTick] = useState(0)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const handler = () => setPermissionsTick((n) => n + 1)
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, handler)
    return () => window.removeEventListener(PERMISSIONS_UPDATED_EVENT, handler)
  }, [])

  return useMemo(() => {
    // Translate once per language change.
    const localizedModules = moduleList.map((m) => ({
      ...m,
      label: t(m.labelKey),
    }))

    const topLevel = localizedModules
      .filter((m) => m.parent === null)
      .sort((a, b) => a.order - b.order)

    return topLevel.map((m) => buildWithChildren(m, localizedModules)).filter(Boolean)
  }, [t, lang, permissionsTick])
}

// Legacy exports kept so older imports still compile. These return the
// raw registry list (no permission filtering, no localized labels) and
// are useful for static lookups; for the live sidebar always use
// `useNavigationConfig`.
export const navigationItems = moduleList
export const navigationConfig = moduleList

// `modules` re-exported for convenience — anything that wants to
// iterate the registry without importing from `src/modules` can pull it
// through here.
export { modules }
